import environment from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { RewardContentType } from 'core/enums/rewards';
import { StarCalc } from 'core/models/red-oxygen/star-calc';
import { CompetitionsDTO, RewardBaseDTO, RewardStatsDTO } from 'core/models/rewards/reward.dto';
import { RewardContentResponse } from 'core/models/rewards/rewards';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';
import { HypermediaObject, HypermediaUtils } from 'utils/hypermedia.utils';

export interface GetRewardsResponse {
  activeCompetitions: CompetitionsDTO[]
  tier: {
    name: string;
    dateAchieved: string;
    calc: StarCalc;
    maxCalc: StarCalc;
    rewards: RewardBaseDTO[],
    requirement: {
      points: string;
    }
  }
  nextTier: {
    name: string;
    rewards: RewardBaseDTO[],
    requirement: {
      points: string;
    }
  }
  stats: RewardStatsDTO
}

export interface GetClaimedRewardsResponse {
  rewards: RewardBaseDTO[];
  competitions: CompetitionsDTO[];
}

export class RewardsService {
  private static apiBuilder = new EndpointHelper(`${environment.UMBRACO_API_URI}/rewards`);
  private static contentBuilder = new EndpointHelper(`${environment.UMBRACO_API_URI}/selfServiceContent`);
  private static rewardsContentBuilder = new EndpointHelper(`${environment.UMBRACO_API_URI}/rewardscontent`);

  static optIntoRewards(customerNumber: string, country: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam(country)
      .addRouteParam(customerNumber)
      .addRouteParam('optInEvent')
      .build();

    return HttpClient.GetInstance.post<unknown, HypermediaObject>(url)
      .then(res => HypermediaUtils.ParseResponseToObject<any>(res?.data))
  }

  static optOutOfRewards(customerNumber: string, country: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam(country)
      .addRouteParam(customerNumber)
      .addRouteParam('optOutEvent')
      .build();

    return HttpClient.GetInstance.post<unknown, HypermediaObject>(url)
      .then(res => HypermediaUtils.ParseResponseToObject<any>(res?.data))
  }

  static getRewards(customerNumber: string, country: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam(country)
      .addRouteParam(customerNumber)
      .addRouteParam('getInfoEvent')
      .build();


    const storedItems = (StorageHelper<GetRewardsResponse>).GetInstance.getFromCache('req', url, CacheAreaKey.Rewards);
    if (storedItems) {
      return Promise.resolve(storedItems as GetRewardsResponse);
    }

    return HttpClient.GetInstance.get<HypermediaObject>(url)
      .then(res => (
        StorageHelper<GetRewardsResponse>).GetInstance
        .setCacheAndReturn(
          'req',
          CacheAreaKey.Rewards,
          HypermediaUtils.ParseResponseToObject<GetRewardsResponse>(res?.data),
          url
        ) as GetRewardsResponse
      );
  }

  static claimHistory(customerNumber: string, country: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam(country)
      .addRouteParam(customerNumber)
      .addRouteParam('claimHistoryEvent')
      .build();


    const storedItems = (StorageHelper<GetClaimedRewardsResponse>).GetInstance.getFromCache('req', url, CacheAreaKey.RewardsHistory);
    if (storedItems) {
      return Promise.resolve(storedItems as GetClaimedRewardsResponse);
    }

    return HttpClient.GetInstance.get<HypermediaObject>(url)
      .then(res => (
        StorageHelper<GetClaimedRewardsResponse>).GetInstance
        .setCacheAndReturn(
          'req',
          CacheAreaKey.RewardsHistory,
          HypermediaUtils.ParseResponseToObject<GetClaimedRewardsResponse>(res?.data),
          url
        ) as GetClaimedRewardsResponse
      );
  }

  static claimEvent(customerNumber: string, country: string, bodyParams: { AgreementId?: string, RewardId?: string, CompetitionName?: string, AuxField?: string }): Promise<any> {
    const url = this.apiBuilder.createUrl()
      .addRouteParam(country)
      .addRouteParam(customerNumber)
      .addRouteParam('claimEvent')
      .build();

    return HttpClient.GetInstance.post<unknown, HypermediaObject>(url, bodyParams)
      .then(res => HypermediaUtils.ParseResponseToObject<any>(res?.data))
  }

  static resendClaimEvent(customerNumber: string, country: string, bodyParams: { RewardId?: string }): Promise<any> {
    const url = this.apiBuilder.createUrl()
      .addRouteParam(country)
      .addRouteParam(customerNumber)
      .addRouteParam('resendClaimEvent')
      .build();

    return HttpClient.GetInstance.post<unknown, HypermediaObject>(url, bodyParams)
      .then(res => HypermediaUtils.ParseResponseToObject<any>(res?.data))
  }

  static getRewardsContent(): Promise<RewardContentResponse> {
    const url = this.rewardsContentBuilder
      .createUrl("get")
      .addQueryParam("version", "2")
      .build();

    const cacheKey = 'content';
    const areaKey = CacheAreaKey.Content;
    const innerKey = RewardContentType.Rewards;

    const storage = (StorageHelper<RewardContentResponse>).GetInstance;

    const storeResponse = storage.getFromCache(cacheKey, innerKey, areaKey);

    if (storeResponse) {
      return Promise.resolve(storeResponse as RewardContentResponse);
    }

    return HttpClient.GetInstance.get<RewardContentResponse>(url).then(response => {
      return storage.setCacheAndReturn(
        cacheKey,
        areaKey,
        response.data,
        innerKey
      ) as RewardContentResponse;
    });
  }
}